import { useAppSelector } from "@/app/hooks";
import { Property } from "@/features/survey/types";
import ButtonComments from "@/shared/components/ButtonComments";
import ShortlistToggleButton from "@/shared/components/ButtonShortlist";
import MediaDropdownPill from "@/shared/components/MediaDropdownPill";
import { usePropertyImage } from "@/shared/hooks/usePropertyImage";
import {
  flattenMediaFilesWithModifiedFiles,
  getPropertyMarkerContent,
  hasAddressLine2AsCustom,
} from "@/shared/utils";
import { getIconData } from "@/shared/utils/map";
import { isFieldVisible } from "@/shared/utils/propertyFieldRules";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";

interface PropertyCardProps {
  property: Property;
  onClick?: () => void;
  isSelected?: boolean;
  classStyleName?: string;
  imageClassName?: string;
  showActions?: boolean;
  actionButtonClassName?: string;
}

/**
 * PropertyCard Component
 *
 * Reusable component for displaying property cards with images
 * Used in Market Map side panel and Property Comparison page
 *
 * Features:
 * - Property image display with loading states
 * - Optional property details section
 * - Integrated action buttons (shortlist and comment)
 * - Responsive design
 */
const PropertyCard = ({
  property,
  isSelected,
  classStyleName = "",
  showActions = false,
}: PropertyCardProps) => {
  // Fetch property image using custom hook
  const { imageSrc, isLoading } = usePropertyImage(property);

  // Get all properties from Redux state for numbering calculation
  const { currentSurvey, mapFilters } = useAppSelector(
    (state) => state.interactiveSurvey,
  );
  const allProperties = mapFilters.showShortlistOnly
    ? currentSurvey?.properties?.filter(
        (p) => p.isClientShortlist && !p.isHidden,
      ) || []
    : currentSurvey?.properties?.filter((p) => !p.isHidden) || [];
  const [markerContent, setMarkerContent] = useState<string | null>(null);
  const [shouldShowMarker, setShouldShowMarker] = useState<boolean>(false);

  useEffect(() => {
    setMarkerContent(getPropertyMarkerContent(property, allProperties));
    setShouldShowMarker(markerContent !== "");
  }, [property, mapFilters]);

  const mediaFiles = useAppSelector((state) => state.media.mediaFiles);
  const propertyMediaFiles = mediaFiles?.[property.id];

  const visibleFlyers = currentSurvey?.isIncludeFlyersPC
    ? flattenMediaFilesWithModifiedFiles(
        (propertyMediaFiles?.fliers ?? []).filter((f) => !f.isHidden),
      )
    : [];
  const visibleFloorPlans = currentSurvey?.isIncludeSitePlansPC
    ? flattenMediaFilesWithModifiedFiles(
        (propertyMediaFiles?.floorPlans ?? []).filter((f) => !f.isHidden),
      )
    : [];

  const navigate = useNavigate();

  // Handle card click - don't propagate if clicking on action buttons
  const handleCardClick = (propertyId: string) => {
    if (!isMarketMapView) {
      navigate(`/interactive/${currentSurvey?.id}/property/${propertyId}`);
    }
  };

  // Determine if the name/address1 fields are toggled on in field rules.
  // Default to true when no field rules are configured (fallback-safe).
  const hasFieldRules =
    !!currentSurvey?.surveyPropertyFieldRule?.propertyFieldRuleList?.length;
  const nameFieldVisible = hasFieldRules
    ? isFieldVisible(currentSurvey, "name")
    : true;
  const address1FieldVisible = hasFieldRules
    ? isFieldVisible(currentSurvey, "address1")
    : true;
  const cityFieldVisible = hasFieldRules
    ? isFieldVisible(currentSurvey, "city")
    : true;
  const stateFieldVisible = hasFieldRules
    ? isFieldVisible(currentSurvey, "stateProvince")
    : true;
  const postalCodeFieldVisible = hasFieldRules
    ? isFieldVisible(currentSurvey, "postalCode")
    : true;
  const address2FieldVisible = hasFieldRules
    ? isFieldVisible(currentSurvey, "address2") ||
      isFieldVisible(currentSurvey, "Address Line 2")
    : true;
  const hasAddressLine2Custom = currentSurvey
    ? hasAddressLine2AsCustom(currentSurvey)
    : false;

  // Title: use property name when the field is visible AND name has real data;
  // otherwise fall back to address1 (always shown at least once)
  const propertyNameValue = property.name || property.propertyName;
  const hasRealName =
    !!propertyNameValue &&
    propertyNameValue.trim() !== "" &&
    propertyNameValue !== "Untitled Property";
  const nameIsTitle = nameFieldVisible && hasRealName;

  const displayTitle = nameIsTitle
    ? propertyNameValue!
    : property.address1 || "Unnamed Property";

  // Address line: when name is title include address1 (if visible) + city/state/zip;
  // when address1 is already the title show only city/state/zip to avoid duplication
  const formatAddress = (): string => {
    const stateZip = [
      stateFieldVisible ? property.stateProvince : null,
      postalCodeFieldVisible ? property.postalCode : null,
    ]
      .filter(Boolean)
      .join("  ");
    const cityStateZip = [
      cityFieldVisible ? property.city : null,
      stateZip || null,
    ].filter(Boolean);

    if (nameIsTitle) {
      const parts = [
        address1FieldVisible ? property.address1?.replace(/,+\s*$/, "") : null,
        ...cityStateZip,
      ].filter(Boolean);
      return parts.join(", ");
    }

    // address1 is already the title — omit it from the address line
    return cityStateZip.join(", ");
  };

  const formatAddress2WithCityStateZip = (): string => {
    const stateZip = [
      stateFieldVisible ? property.stateProvince : null,
      postalCodeFieldVisible ? property.postalCode : null,
    ]
      .filter(Boolean)
      .join("  ");
    const address2 = property.address2?.replace(/,+\s*$/, "");
    const parts = [
      address2 === "—" ? "" : address2,
      cityFieldVisible ? property.city : null,
      stateZip || null,
    ].filter(Boolean);

    return parts.join(", ");
  };
  const shouldUseAddress2Layout =
    !hasAddressLine2Custom && address2FieldVisible;

  const { iconColor } = getIconData(property);
  const location = useLocation();
  const isMarketMapView = location.pathname.includes("marketmap");
  const getSectionForDocPill = () => {
    const floorPlanLabel = currentSurvey?.sitePlanLabel || "Site Plans";
    const flyerLabel = currentSurvey?.flyerLabel || "Flyers";
    if (visibleFlyers.length > 0 && visibleFloorPlans.length > 0) {
      return [
        { label: floorPlanLabel, files: visibleFloorPlans },
        { label: flyerLabel, files: visibleFlyers },
      ];
    }
    if (visibleFlyers.length > 0) {
      return [{ label: flyerLabel, files: visibleFlyers }];
    }
    if (visibleFloorPlans.length > 0) {
      return [{ label: floorPlanLabel, files: visibleFloorPlans }];
    }
    return [];
  };
  return (
    <div
      className={`property-card ${classStyleName}`}
      style={{
        borderColor: isSelected
          ? "var(--color-cbre-green-light)"
          : "transparent",
        borderWidth: isSelected ? "2px" : "1px",
      }}
    >
      <div className="property-card-details">
        {/* Property Image */}
        {isLoading ? (
          <div className="bg-gray-200 animate-pulse h-48"></div>
        ) : (
          <div
            onClick={() => handleCardClick(property.id)}
            style={{ backgroundImage: `url(${imageSrc})` }}
            className="prop-bg-image"
          >
            {/* property pen */}
            {shouldShowMarker && (
              <div
                className="prop-pen"
                style={{
                  backgroundColor: iconColor,
                  margin: "6px",
                }}
              >
                {markerContent === "starIcon" ? (
                  <i
                    className="emerald-icon selectedIconImage material-icons star-icon"
                    aria-hidden="false"
                    title="Star Rate"
                  >
                    star_rate
                  </i>
                ) : (
                  markerContent
                )}
              </div>
            )}
          </div>
        )}

        <div className="property-details">
          <div className="flex flex-col gap-y-1">
            <h3 className="fancy prop-title truncate">{displayTitle}</h3>
            {shouldUseAddress2Layout ? (
              <div
                className="prop-card-address"
                style={{ color: "var(--color-default)" }}
              >
                {nameIsTitle && address1FieldVisible && property.address1 && (
                  <p className="line-clamp-1">
                    {property.address1.replace(/,+\s*$/, "")}
                  </p>
                )}
                <p className="line-clamp-1">
                  {formatAddress2WithCityStateZip()}
                </p>
              </div>
            ) : (
              <p
                className="prop-card-address line-clamp-2"
                style={{ color: "var(--color-default)" }}
              >
                {formatAddress()}
              </p>
            )}
          </div>
          {isMarketMapView && showActions && (
            <div className="flex gap-x-3">
              <ShortlistToggleButton property={property} size="Small" />
              <ButtonComments property={property} size="Small" />
            </div>
          )}
        </div>
      </div>
      {/* Pills and Shortlist/Comments always in same row */}
      {!isMarketMapView && (
        <div className="flex flex-wrap items-center gap-2">
          {/* Desktop: separate pills */}
          {(visibleFlyers.length > 0 || visibleFloorPlans.length > 0) && (
            <>
              <div className="hidden lg:flex gap-1.5 my-4 w-full">
                {visibleFloorPlans.length > 0 && (
                  <MediaDropdownPill
                    icon="layers"
                    label={currentSurvey?.sitePlanLabel || "Site Plans"}
                    files={visibleFloorPlans}
                  />
                )}
                {visibleFlyers.length > 0 && (
                  <MediaDropdownPill
                    icon="menu_book"
                    label={currentSurvey?.flyerLabel || "Flyers"}
                    files={visibleFlyers}
                  />
                )}
              </div>
              {/* Mobile: single Docs pill combining flyers + floor plans */}
              <div className="flex lg:hidden my-4 w-full">
                <MediaDropdownPill
                  icon="file_present"
                  label="Docs"
                  sections={getSectionForDocPill()}
                />
              </div>
            </>
          )}
          {/* Shortlist and Comments shown if showActions (non-market-map view only; market map renders them inline above) */}
          {showActions && (
            <div className="flex gap-x-3">
              <ShortlistToggleButton property={property} size="Small" />
              <ButtonComments property={property} size="Small" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PropertyCard;
